import React, { useEffect, useState } from "react";
import { api, extractError } from "../../lib/api";
import { usePageBootstrap } from "../../hooks/useBootstrapList";
import { useBootstrapWS } from "../../hooks/useBootstrapWS";
import { SectionCard } from "../Ui";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { FormActions, FormField, FormSection } from "../forms/FormField";
import {
  PLANT_REPORT_TYPES,
  buildPlantPayload,
  defaultPlantRows,
} from "../../lib/plantLabConfig";
import LabTabShell from "./LabTabShell";
import PlantLabRegister from "./PlantLabRegister";

export function PlantLabTab() {
  const { data, reload } = usePageBootstrap({
    path: "/lab/bootstrap",
    params: { tab: "plant" },
    wsTypes: ["lab"],
  });
  useBootstrapWS(["lab"], reload);
  const plantReports = data?.reports || [];
  const [plantType, setPlantType] = useState("HUSKER");
  const [plantRows, setPlantRows] = useState(() => defaultPlantRows("HUSKER"));
  const [plantNotes, setPlantNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setPlantRows(defaultPlantRows(plantType));
  }, [plantType]);

  const updateRow = (i, field, value) => {
    setPlantRows((rows) => rows.map((row, j) => (j === i ? { ...row, [field]: value } : row)));
  };
  const addRow = () => setPlantRows((rows) => [...rows, ...defaultPlantRows(plantType)]);
  const removeRow = (i) => setPlantRows((rows) => (rows.length > 1 ? rows.filter((_, j) => j !== i) : rows));

  const submit = async (e) => {
    e.preventDefault();
    let payload;
    try {
      payload = buildPlantPayload(plantType, plantRows);
    } catch (ve) {
      toast.error(ve.message || "Check form values");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/plant-lab-reports", { report_type: plantType, payload, notes: plantNotes });
      toast.success("In-plant report saved");
      setPlantNotes("");
      setPlantRows(defaultPlantRows(plantType));
      await reload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const typeMeta = PLANT_REPORT_TYPES.find((x) => x.value === plantType);

  const record = (
      <SectionCard title="New in-plant quality report">
        <form onSubmit={submit} className="space-y-4">
          <FormSection title="Report type">
            <FormField label="Type" hint={typeMeta?.hint}>
              <Select value={plantType} onValueChange={setPlantType}>
                <SelectTrigger className="w-full max-w-md">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLANT_REPORT_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          </FormSection>

          <FormSection
            title="Line items"
            actions={
              <Button type="button" variant="outline" size="sm" onClick={addRow}>
                Add row
              </Button>
            }
          >
            {plantType === "HUSKER" &&
              plantRows.map((row, i) => (
                <PlantRow key={i} onRemove={() => removeRow(i)} cols={4}>
                  <FormField label="Husker label (optional)" compact>
                    <Input value={row.husker_label} onChange={(e) => updateRow(i, "husker_label", e.target.value)} placeholder="Husker 1" />
                  </FormField>
                  <FormField label="Broken %" compact>
                    <Input type="number" step="0.01" value={row.broken} onChange={(e) => updateRow(i, "broken", e.target.value)} />
                  </FormField>
                  <FormField label="Efficiency %" compact>
                    <Input type="number" step="0.01" value={row.efficiency} onChange={(e) => updateRow(i, "efficiency", e.target.value)} />
                  </FormField>
                </PlantRow>
              ))}
            {plantType === "WHITNER" &&
              plantRows.map((row, i) => (
                <PlantRow key={i} onRemove={() => removeRow(i)} cols={4}>
                  <FormField label="Whitner label (optional)" compact>
                    <Input value={row.unit_label} onChange={(e) => updateRow(i, "unit_label", e.target.value)} />
                  </FormField>
                  <FormField label="Broken %" compact>
                    <Input type="number" step="0.01" value={row.broken} onChange={(e) => updateRow(i, "broken", e.target.value)} />
                  </FormField>
                  <FormField label="Kett" compact>
                    <Input type="number" step="0.01" value={row.kett} onChange={(e) => updateRow(i, "kett", e.target.value)} />
                  </FormField>
                </PlantRow>
              ))}
            {plantType === "LENGTH_SCANNER" &&
              plantRows.map((row, i) => (
                <PlantRow key={i} onRemove={() => removeRow(i)} cols={5}>
                  <FormField label="Length (mm)" compact>
                    <Input type="number" step="0.01" value={row.length_mm} onChange={(e) => updateRow(i, "length_mm", e.target.value)} />
                  </FormField>
                  <FormField label="Scanner reading" compact>
                    <Input value={row.scanner_reading} onChange={(e) => updateRow(i, "scanner_reading", e.target.value)} />
                  </FormField>
                  <FormField label="Grade" compact>
                    <Input value={row.grade} onChange={(e) => updateRow(i, "grade", e.target.value)} />
                  </FormField>
                  <FormField label="Remark" compact>
                    <Input value={row.remark} onChange={(e) => updateRow(i, "remark", e.target.value)} />
                  </FormField>
                </PlantRow>
              ))}
            {plantType === "KETT" &&
              plantRows.map((row, i) => (
                <PlantRow key={i} onRemove={() => removeRow(i)} cols={3}>
                  <FormField label="Kett" compact>
                    <Input type="number" step="0.01" value={row.kett} onChange={(e) => updateRow(i, "kett", e.target.value)} />
                  </FormField>
                  <FormField label="Machinery name" compact>
                    <Input value={row.machinery_name} onChange={(e) => updateRow(i, "machinery_name", e.target.value)} />
                  </FormField>
                </PlantRow>
              ))}
            {plantType === "DRYER" &&
              plantRows.map((row, i) => (
                <PlantRow key={i} onRemove={() => removeRow(i)} cols={5}>
                  <FormField label="Broken %" compact>
                    <Input type="number" step="0.01" value={row.broken} onChange={(e) => updateRow(i, "broken", e.target.value)} />
                  </FormField>
                  <FormField label="Crackness %" compact>
                    <Input type="number" step="0.01" value={row.crackness} onChange={(e) => updateRow(i, "crackness", e.target.value)} />
                  </FormField>
                  <FormField label="Dryer #" compact>
                    <Input value={row.dryer_number} onChange={(e) => updateRow(i, "dryer_number", e.target.value)} />
                  </FormField>
                  <FormField label="Lot #" compact>
                    <Input value={row.lot_number} onChange={(e) => updateRow(i, "lot_number", e.target.value)} />
                  </FormField>
                </PlantRow>
              ))}
            {plantType === "DYNAMIC" &&
              plantRows.map((row, i) => (
                <PlantRow key={i} onRemove={() => removeRow(i)} cols={3}>
                  <FormField label="Parameter name" compact>
                    <Input value={row.name} onChange={(e) => updateRow(i, "name", e.target.value)} />
                  </FormField>
                  <FormField label="Value" compact>
                    <Input value={row.value} onChange={(e) => updateRow(i, "value", e.target.value)} />
                  </FormField>
                </PlantRow>
              ))}
          </FormSection>

          <FormField label="Notes (optional)">
            <Input value={plantNotes} onChange={(e) => setPlantNotes(e.target.value)} placeholder="Shift, batch, remarks…" />
          </FormField>

          <FormActions>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-bansal-sage hover:bg-bansal-sageDark text-white sm:min-w-[180px]"
            >
              {submitting ? "Saving…" : "Save in-plant report"}
            </Button>
          </FormActions>
        </form>
      </SectionCard>
  );

  const register = <PlantLabRegister reports={plantReports} />;

  return <LabTabShell record={record} register={register} registerCount={plantReports.length} testId="plant-lab-tabs" />;
}

function PlantRow({ children, onRemove, cols = 4 }) {
  const gridClass =
    cols === 5
      ? "grid-cols-1 md:grid-cols-5"
      : cols === 3
        ? "grid-cols-1 md:grid-cols-3"
        : "grid-cols-1 md:grid-cols-4";
  return (
    <div className={`grid ${gridClass} gap-3 items-end border-t border-border pt-3 first:border-t-0 first:pt-0`}>
      {children}
      <Button type="button" variant="ghost" size="sm" className="justify-self-end" onClick={onRemove}>
        Remove
      </Button>
    </div>
  );
}
